'use client';
import { Dispatch, useMemo } from 'react';
import QRCodeModal from '@/components/modal/QRCodeModal/QRCodeModal';
import { useModal } from '@/components/hook/modal';
import { addItemToCart, removeItemFromCart } from '@/util/cart_generic_client';
import cn from 'classnames';
import { useCart } from '@/components/hook/cart';

import { EllaAction, EllaState } from '../Ella/Ella';

import {
  PRODUCT_CARD_TYPE,
  ProductCardContent,
  ProductCardProps
} from '@/components/common/ProductCard/ProductCard';

import analytics from '@/util/analytics';
import toast from 'react-hot-toast';

import EllaSelectProductIcon from '@/components/common/EllaSelectProductIcon/EllaSelectProductIcon';
import WishlistIcon from '@/components/common/WishlistIcon/WishlistIcon';
import { useUser } from '@/components/hook/user';
import { useEllaAnalytics } from '@/components/hook/analytics';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

import './ProductCardSelect.style.scss';

import { ProductCardData } from '@/types/Product';
import { usePathname } from 'next/navigation';
import { CLOSET_SLOT_TIER_SLUG, ClosetValidationStatus } from '@/types/Closet';
import { useClosetValidationForProductTier } from '@/components/hook/closet';
import { productUrlIOSDeepLink } from '@/util/iosDeepLinks';

interface ProductCardSelectProps extends ProductCardProps {
  dispatch: Dispatch<EllaAction>;
  state: EllaState;
  slotsOpen?: string[];
  isUserInLimitedState?: boolean;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
  isDisabled: boolean;
  replaceItemInCart?: (value: any) => void;
}

const getMiddleSize = (sizeStr: string | undefined): string => {
  if (!sizeStr) return '';
  try {
    // Handle string array format like '[XS]' or '[XS, S, M]'
    const cleanStr = sizeStr.replace(/[\[\]\s]/g, '');
    const sizes = cleanStr.split(',');
    if (sizes.length === 0) return '';
    const middleIndex = Math.floor(sizes.length / 2);
    return sizes[middleIndex].toString();
  } catch (e) {
    console.error('Error parsing sizes:', e);
    return '';
  }
};

export default function ProductCardSelect(props: ProductCardSelectProps) {
  const modalTools = useModal();
  const {
    className = '',
    dispatch,
    small = false,
    data,
    price,
    productType,
    variant = '',
    state,
    slotsOpen,
    isUserInLimitedState = false,
    isProcessing = false,
    setIsProcessing,
    isDisabled = false,
    replaceItemInCart
  } = props;
  const { value: cartValue } = useCart();

  const {
    loading: closetValidationLoading,
    closetValidationStatus,
    closetValidationSwapDate,
    revalidate
  } = useClosetValidationForProductTier(
    data.id,
    data.tier.toLowerCase() as CLOSET_SLOT_TIER_SLUG,
    cartValue
  );

  const productIsSelected: boolean = !!state?.cart?.find(
    (item) =>
      item.sku === data?.sku || item.item_group_id === data?.item_group_id
  );
  const { value: user } = useUser();
  const { metadata } = useEllaAnalytics();

  const handleWishlistAnalytics = (item: ProductCardData) => {
    if (!user) {
      analytics.track('Ella AI Add to Wishlist (temporary) - Clicked', {
        ...metadata,
        parentSku: item.sku || item.item_group_id,
        parentSkuTier: item.tier
      });
    } else {
      analytics.track('Ella AI Add to Wishlist - Clicked', {
        ...metadata,
        parentSku: item.sku || item.item_group_id,
        parentSkuTier: item.tier
      });
    }
  };

  const handleProductSelect = async (action: 'ADD' | 'REMOVE') => {
    if (isProcessing || state.isProcessing) return;

    // Set loading state for this specific product
    setIsProcessing(true);

    try {
      const cartItem = {
        ...data,
        sku: data?.item_group_id ?? '',
        price: (price || 0) * 100,
        allSizes: data?.size ?? '',
        size: getMiddleSize(data?.size) || ''
      };

      if (action === 'ADD') {
        // Get the current cartId from state if it exists
        const cartData = await addItemToCart(
          props.state?.revolveCartId ?? undefined,
          cartItem,
          props.state?.bill?.taxRate
        );
        if (cartData && 'error' in cartData && cartData.error) {
          toast.error(
            "Sorry, there's been an error. Refresh to start a new chat."
          );
          return;
        }
        if (cartData && !('error' in cartData)) {
          if (cartData.revolveCartId) {
            dispatch({
              type: 'SET_CART_ID',
              payload: { revolveCartId: cartData.revolveCartId }
            });
          }
          cartItem.id = cartData.cartItemId ?? cartItem.id;
          cartItem.taxItemId = cartData.taxItemId;
        }
        dispatch({
          type: 'ADD_TO_CART',
          payload: { cartItem: cartItem }
        });
      } else {
        const item = props.state?.cart?.find(
          (item) => item.sku === cartItem.sku
        );

        if (!item) {
          return;
        }
        await removeItemFromCart(
          props.state?.revolveCartId ?? undefined,
          item.id,
          item.store
        );
        dispatch({
          type: 'REMOVE_FROM_CART',
          payload: { cartItemId: item.id }
        });
      }
    } finally {
      // Reset loading state for this specific product
      setIsProcessing(false);
    }
  };

  let productUrl: string | undefined;
  const { isIOSWebView } = useContextBroker();
  const isEllaInStore = usePathname() === '/ella-in-store';

  if (data.cj_link) {
    productUrl = data.cj_link;
  } else if (isIOSWebView) {
    productUrl = productUrlIOSDeepLink(data.sku);
  } else {
    productUrl = `/products/${data.slug}`;
  }

  const isAbleToSelect = useMemo(() => {
    let isAbleToSelectVivrelle = isUserInLimitedState
      ? false
      : data?.store === 'vivrelle'
      ? slotsOpen && slotsOpen.length > 0
      : true;

    // Edge cases
    if (!isAbleToSelectVivrelle) return false;
    if (data?.store !== 'vivrelle') return isAbleToSelectVivrelle;

    if (!closetValidationStatus) return true;

    return false;
  }, [state.cart]);

  return (
    <div
      className={cn(
        'ProductCardSelect',
        className,
        variant,
        { small },
        { selected: productIsSelected },
        { 'is-disabled': isDisabled }
      )}
      onClick={() => {
        if (productType !== PRODUCT_CARD_TYPE.REVOLVE) {
          analytics.track('Ella AI Vivrelle Item - Clicked', {
            ...metadata,
            parentSku: data.sku || data.item_group_id,
            parentSkuTier: data.tier
          });
        } else {
          analytics.track('Ella AI Revolve/FWRD Item - Clicked', {
            ...metadata,
            itemOrigin: data.store,
            brand: data.brand,
            item_group_id: data.item_group_id,
            updated_title: data.name,
            current_price: data.price,
            category: data.category,
            link: data.link,
            size: data.size,
            store: data.store
          });
        }

        if (isUserInLimitedState && productUrl) {
          if (isEllaInStore) {
            modalTools.showModal(
              <QRCodeModal
                url={productUrl}
                product={data}
                onClose={modalTools.closeModal}
              />
            );
          } else {
            window.open(productUrl, '_blank');
          }
        }
      }}
      style={isUserInLimitedState && productUrl ? { cursor: 'pointer' } : {}}
    >
      <ProductCardContent
        data={data}
        price={price}
        small={small}
        variant={variant}
        productType={productType}
        productSelected={productIsSelected}
        handleProductSelect={(action: 'ADD' | 'REMOVE') => {
          if (isDisabled) return Promise.resolve();

          // Only display replace item if user is trying to add a new item and the item is the same tier
          if (
            closetValidationStatus === ClosetValidationStatus.EmptyCart &&
            action === 'ADD' &&
            replaceItemInCart
          ) {
            setIsProcessing(true);
            replaceItemInCart(data);
            return Promise.resolve();
          }

          if (!isAbleToSelect && action === 'REMOVE') {
            return handleProductSelect(action);
          }

          if (
            action === 'ADD' &&
            !isProcessing &&
            user &&
            closetValidationStatus
          ) {
            dispatch({
              type: 'LIMIT_REACHED',
              payload: { limitReached: true }
            });
          }

          if (!closetValidationStatus) {
            return handleProductSelect(action);
          }

          return Promise.resolve();
        }}
      />

      {/* For Vivrelle products, use WishlistIcon, for limited state show nothing on revolve items*/}
      {productType !== PRODUCT_CARD_TYPE.REVOLVE &&
      !isEllaInStore &&
      (isUserInLimitedState ||
        (slotsOpen && slotsOpen.length === 0) ||
        !slotsOpen?.includes(data?.tier.toLowerCase())) ? (
        <div>
          <WishlistIcon
            data={data}
            useTemporary={!user}
            dispatch={dispatch}
            wishlistAnalytics={handleWishlistAnalytics}
          />
        </div>
      ) : (
        !isUserInLimitedState && (
          <div className={cn('icon-wrapper')}>
            <EllaSelectProductIcon
              selected={productIsSelected}
              isProcessing={isProcessing}
            />
          </div>
        )
      )}
    </div>
  );
}
